<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import {
  VButton,
  VTextField,
  VDropdownMultiple,
  VIcon,
  VLoadingSection,
  SelectCards,
  VTextArea,
} from '@/modules/shared/components'
import VSelect from '@/modules/investing/components/v-select.vue'
import TeamMemberList from './team-member-list.vue'
import { useExtendedI18n } from '@/i18n'
import { initialMoney, toMoney, toNumber } from '@/modules/shared/utils/money'
import { helpers, required } from '@vuelidate/validators'
import { Investor } from '@/modules/investing/stores/investor-store'

///////////////////////////////////////////////////////////////////////////////
// CONSTANTS
///////////////////////////////////////////////////////////////////////////////

const ACCREDITATION_LEVELS = [
  {
    label: 'Non-accredited',
    value: 'non_accredited',
    description: '',
  },
  {
    label: 'Accredited',
    value: 'accredited',
    description: '',
  },
  {
    label: 'Qualified Purchaser',
    value: 'qualified_purchaser',
    description: '',
  },
]

const INVESTMENT_SIZES = [
  '$10,000 - $50,000',
  '$50,000 - $100,000',
  '$100,000 - $250,000',
  '$250,000 - $500,000',
  '$500,000 - $1,000,000',
  '$1,000,000+',
]

const INVESTMENT_SIZE_LABELS: Record<string, string> = {
  '$10,000 - $50,000': '$10K–$50K',
  '$50,000 - $100,000': '$50K–$100K',
  '$100,000 - $250,000': '$100K–$250K',
  '$250,000 - $500,000': '$250K–$500K',
  '$500,000 - $1,000,000': '$500K–$1M',
  '$1,000,000+': '$1M+',
}

const INDUSTRIES = [
  'Adtech',
  'Aerospace',
  'AI / ML',
  'Analytics',
  'Biotech',
  'Blockchain / Crypto',
  'Cannabis',
  'Cleantech',
  'Collaboration Tools',
  'Consumer',
  'Consumer Product',
  'Dating',
  'Developer Tools',
  'Drones',
  'E-Commerce',
  'Education',
  'Enterprise',
  'Finance',
  'Fitness',
  'Food / Beverages',
  'Gaming',
  'Hardware',
  'Health',
  'Healthcare',
  'Home Services',
  'HR & Recruiting',
  'SaaS',
  'Insurance',
  'Investment Platforms',
  'Internet of Things',
  'Logistics',
  'Media / Entertainment',
  'Medical Devices',
  'Non Tech',
  'On Demand',
  'Payments',
  'Productivity',
  'Real Estate',
  'Retail',
  'Robotics',
  'Sales Tools',
  'Security',
  'Social',
  'Social Impact',
  'Transportation',
  'Travel',
  'VR / AR',
]

const INVESTMENT_TYPES = [
  {
    value: 'fund',
    label: 'Fund',
    description: 'Investment manager chooses the investments',
  },
  {
    value: 'syndicate',
    label: 'Syndicate',
    description: 'You choose the investments and invest with others through SPVs',
  },
  {
    value: 'angel_group',
    label: 'Angel Group',
    description: 'You choose the companies and invest directly',
  },
]

const INVESTMENT_STAGES = ['Pre-Seed', 'Seed', 'Early', 'Growth', 'Pre-IPO', 'Secondaries']

///////////////////////////////////////////////////////////////////////////////
// MAIN
///////////////////////////////////////////////////////////////////////////////

const props = withDefaults(
  defineProps<{
    candidate: any
    inDisplayMode?: boolean
    isWizard?: boolean
    universities: any[]
    skeleton?: boolean
    candidate_for?: 'Investor' | 'Group'
    cardMode?: boolean
    investors?: Investor[]
    groupAccreditationLevelMin?: string | null
    onSubmit: (payload: any) => Promise<void>
  }>(),
  {
    candidate_for: 'Investor',
    groupAccreditationLevelMin: null,
  },
)

const { n } = useExtendedI18n()

const isWizard = computed(() => !!props.isWizard)
const isFieldDisabled = computed(() => !!props.inDisplayMode)
const currentCandidate = computed(() => props.candidate)

const isForGroup = computed(() => props.candidate_for === 'Group')
const isForInvestor = computed(() => props.candidate_for === 'Investor')

///////////////////////////////////////////////////////////////////////////////
// Options
///////////////////////////////////////////////////////////////////////////////

const accreditationLevelOptions = computed(() =>
  ACCREDITATION_LEVELS.map((level) => ({
    label: level.label,
    description: level.description,
    value: level.value,
  })),
)

const industryOptions = computed(() =>
  INDUSTRIES.map((industry) => ({
    label: industry,
    value: industry,
    disabled:
      candidateForm.value.industries_interested.includes(industry) ||
      candidateForm.value.industries_excluded.includes(industry),
  })),
)

const investmentStageOptions = computed(() =>
  INVESTMENT_STAGES.map((stage) => ({
    label: stage,
    value: stage,
    description: '',
  })),
)

const investmentSizeOptions = computed(() =>
  INVESTMENT_SIZES.map((size) => ({
    value: size,
    label: INVESTMENT_SIZE_LABELS[size],
    description: '',
  })),
)

const universityOptions = computed(() =>
  props.universities.map((university) => ({
    label: university,
    value: university,
  })),
)

///////////////////////////////////////////////////////////////////////////////
// Form
///////////////////////////////////////////////////////////////////////////////

const candidateInitialState = {
  id: null,
  subject_id: null,
  subject_type: null,
  accreditation_level_min: null,
  deal_investment_min: null,
  deal_investment_range: null,
  fund_investment_min: null,
  fund_investment_range: null,
  spv_investment_min: null,
  spv_investment_range: null,
  industries_interested: [],
  industries_excluded: [],
  investment_stage: [],
  investment_thesis: null,
  investment_types_interested: [],
  university_affiliations: [],
}

const candidateRules = () => {
  if (props.candidate_for === 'Investor') {
    return {
      investment_types_interested: { required },
      deal_investment_range: {
        isRequired: helpers.withMessage('Please select a deal investment amount', (value) =>
          isAngelGroupTypeSelected.value ? !!value : true,
        ),
      },
      fund_investment_range: {
        isRequired: helpers.withMessage('Please select a fund investment amount', (value) =>
          isFundTypeSelected.value ? !!value : true,
        ),
      },
      spv_investment_range: {
        isRequired: helpers.withMessage('Please select a syndicate investment amount', (value) =>
          isSyndicateTypeSelected.value ? !!value : true,
        ),
      },
    }
  }

  return {
    investment_types_interested: { required },
    deal_investment_min: {
      isRequired: helpers.withMessage('Per deal minimum is required', (value) =>
        isAngelGroupTypeSelected.value ? !!value : true,
      ),
    },
    fund_investment_min: {
      isRequired: helpers.withMessage('Fund investment minimum is required', (value) =>
        isFundTypeSelected.value ? !!value : true,
      ),
    },
    spv_investment_min: {
      isRequired: helpers.withMessage('SPV investment minimum is required', (value) =>
        isSyndicateTypeSelected.value ? !!value : true,
      ),
    },
  }
}
const candidateForm = ref({ ...candidateInitialState })
const v$ = useVuelidate(candidateRules(), candidateForm, { $lazy: true })

const isFundTypeSelected = computed(() => !!candidateForm?.value?.investment_types_interested?.includes('fund'))

const isSyndicateTypeSelected = computed(
  () => !!candidateForm?.value?.investment_types_interested?.includes('syndicate'),
)

const isAngelGroupTypeSelected = computed(
  () => !!candidateForm?.value?.investment_types_interested?.includes('angel_group'),
)

const setForm = () => {
  const keys = Object.keys(candidateInitialState)
  keys.forEach((key) => {
    candidateForm.value[key] = currentCandidate.value[key] || candidateInitialState[key]

    if (['deal_investment_min', 'fund_investment_min', 'spv_investment_min'].includes(key) && isForGroup.value) {
      candidateForm.value[key] = !!currentCandidate.value[key]
        ? toMoney(currentCandidate.value[key], 'USD')
        : initialMoney
    }

    // investment_stage is now an array column — ensure it's always an array for groups
    if (key === 'investment_stage' && isForGroup.value) {
      const stageValue = currentCandidate.value[key]
      candidateForm.value[key] = Array.isArray(stageValue) ? stageValue : []
    }

    // For groups, initialize accreditation_level_min from the workspace prop
    if (key === 'accreditation_level_min' && isForGroup.value) {
      candidateForm.value[key] = currentCandidate.value[key] || props.groupAccreditationLevelMin || ''
    }
  })
}

const resetForm = () => {
  v$.value.$reset()
  setForm()
}

watch(
  currentCandidate,
  (value) => {
    if (value) setForm()
  },
  { immediate: true },
)

const isValid = async () => {
  const valid = await v$.value.$validate()
  return valid
}

const submit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  const payload = { ...candidateForm.value }
  if (isForGroup.value) {
    if (payload.fund_investment_min) payload.fund_investment_min = toNumber(payload.fund_investment_min)
    if (payload.deal_investment_min) payload.deal_investment_min = toNumber(payload.deal_investment_min)
    if (payload.spv_investment_min) payload.spv_investment_min = toNumber(payload.spv_investment_min)
  }

  // prettier-ignore
  if (!isFundTypeSelected.value) {
    payload.fund_investment_min = null
    payload.fund_investment_range = null
  }

  // prettier-ignore
  if (!isAngelGroupTypeSelected.value) {
    payload.deal_investment_min = null
    payload.deal_investment_range = null
  }

  // prettier-ignore
  if (!isSyndicateTypeSelected.value) {
    payload.spv_investment_min = null
    payload.spv_investment_range = null
  }

  await props.onSubmit(payload)
}

defineExpose({
  isValid,
  resetForm,
  submit,
})

const errors = computed(() => {
  return {
    industries_interested: v$.value['industries_interested']?.$errors[0]?.$message,
    industries_excluded: v$.value['industries_excluded']?.$errors[0]?.$message,
  }
})
</script>

<template>
  <div :class="cardMode ? 'w-full' : 'w-full rounded-lg border-[1px] p-5'">
    <div class="space-y-6" v-if="!skeleton">
      <!-- ============================================================ -->
      <!-- GROUP LAYOUT (card mode: matching prototype design) -->
      <!-- ============================================================ -->
      <template v-if="isForGroup">
        <!-- Investment Thesis -->
        <div class="mb-5">
          <div class="mb-1.5 flex items-center justify-between">
            <label class="block text-sm font-medium text-gray-700">Investment Thesis</label>
            <div class="flex items-center gap-3">
              <slot name="actions"></slot>
            </div>
          </div>
          <VTextArea
            label=""
            v-model="candidateForm.investment_thesis"
            :disabled="isFieldDisabled"
            property="investment_thesis"
            placeholder="Describe your investment focus, stage, and what kinds of companies you back..."
          />
        </div>

        <!-- Structure -->
        <div class="mb-6">
          <SelectCards
            label="Structure"
            v-model="candidateForm.investment_types_interested"
            :options="INVESTMENT_TYPES.map((t) => ({ ...t, description: '' }))"
            :disabled="isFieldDisabled"
            :multiple="true"
            property="investment_types_interested"
            :v$="v$"
            :columns="3"
          />
        </div>

        <!-- Investment Minimums (directly below structure, aligned with checkboxes) -->
        <div v-if="candidateForm.investment_types_interested.length" class="mb-6">
          <div class="grid grid-cols-3 gap-3">
            <div :class="{ invisible: !isFundTypeSelected }">
              <VTextField
                type="currency"
                v-if="isFundTypeSelected"
                v-model="candidateForm.fund_investment_min"
                label="Minimum investor commitment per fund"
                :placeholder="n(initialMoney, 'currency')"
                property="fund_investment_min"
                :v$="v$"
                align="left"
              />
            </div>
            <div :class="{ invisible: !isSyndicateTypeSelected }">
              <VTextField
                type="currency"
                v-if="isSyndicateTypeSelected"
                v-model="candidateForm.spv_investment_min"
                label="Minimum investor commitment per syndicate (SPV)"
                :placeholder="n(initialMoney, 'currency')"
                property="spv_investment_min"
                :v$="v$"
                align="left"
              />
            </div>
            <div :class="{ invisible: !isAngelGroupTypeSelected }">
              <VTextField
                type="currency"
                v-if="isAngelGroupTypeSelected"
                v-model="candidateForm.deal_investment_min"
                label="Minimum investor commitment per deal"
                :placeholder="n(initialMoney, 'currency')"
                property="deal_investment_min"
                :v$="v$"
                align="left"
              />
            </div>
          </div>
        </div>

        <!-- Accreditation -->
        <div class="mb-6">
          <label class="mb-1 block text-sm font-medium text-gray-700">Accreditation Required</label>
          <p class="mb-2 text-sm text-gray-500">
            Set the minimum accreditation level required from investors connecting with your firm.
          </p>
          <select
            v-model="candidateForm.accreditation_level_min"
            :disabled="isFieldDisabled"
            class="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
          >
            <option value="">Select a level</option>
            <option v-for="level in ACCREDITATION_LEVELS" :key="level.value" :value="level.value">
              {{ level.label }}
            </option>
          </select>
        </div>

        <!-- University Affiliation -->
        <div class="mb-6">
          <label class="mb-1 block text-sm font-medium text-gray-700">University Affiliation</label>
          <p class="mb-2 text-sm text-gray-500">
            If your firm is affiliated with a university alumni network, select the university to match with alumni
            investors.
          </p>
          <VDropdownMultiple
            aligned="left"
            class="z-auto"
            :options="universityOptions"
            property="university_affiliations"
            v-model="candidateForm.university_affiliations"
            :show-selected-list="true"
            :disabled="isFieldDisabled"
          >
            <VButton size="xl" class="w-full" :disabled="isFieldDisabled">
              <div class="flex items-center justify-between">
                <div>Select...</div>
                <VIcon name="chevron_selector_vertical" />
              </div>
            </VButton>
          </VDropdownMultiple>
        </div>

        <!-- Investment Stage -->
        <div class="mb-4 border-b border-gray-200 pb-3 text-xs font-bold uppercase tracking-wide text-gray-500">
          Investment Stage
        </div>
        <div class="mb-6">
          <SelectCards
            v-model="candidateForm.investment_stage"
            :options="investmentStageOptions"
            :disabled="isFieldDisabled"
            :multiple="true"
            property="investment_stage"
            :v$="v$"
            :columns="3"
          />
        </div>

        <!-- Industry Focus -->
        <div class="mb-4 border-b border-gray-200 pb-3 text-xs font-bold uppercase tracking-wide text-gray-500">
          Industry Focus
        </div>
        <div class="mb-6">
          <VDropdownMultiple
            aligned="left"
            class="z-auto"
            :options="industryOptions"
            property="industries_interested"
            v-model="candidateForm.industries_interested"
            :show-selected-list="true"
            :disabled="isFieldDisabled"
          >
            <VButton size="xl" class="w-full" :disabled="isFieldDisabled">
              <div class="flex items-center justify-between">
                <div>Select multiple...</div>
                <VIcon name="chevron_selector_vertical" />
              </div>
            </VButton>
          </VDropdownMultiple>
          <p class="mt-1 text-sm text-red-500" v-if="errors.industries_interested">
            {{ errors.industries_interested }}
          </p>
        </div>

        <!-- Team Members -->
        <div class="mb-4 border-b border-gray-200 pb-3 text-xs font-bold uppercase tracking-wide text-gray-500">
          Key Team Members
        </div>
        <div>
          <TeamMemberList :candidate-id="currentCandidate?.id" :disabled="isFieldDisabled" />
        </div>
      </template>

      <!-- ============================================================ -->
      <!-- INVESTOR LAYOUT (onboarding-style UI) -->
      <!-- ============================================================ -->
      <div v-else>
        <div class="mb-1.5 flex items-center justify-between">
          <div></div>
          <div class="flex items-center gap-3">
            <slot name="actions"></slot>
          </div>
        </div>

        <!-- Investment Type -->
        <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Investment type</div>
        <div class="mb-6">
          <SelectCards
            v-model="candidateForm.investment_types_interested"
            :options="INVESTMENT_TYPES"
            :disabled="isFieldDisabled"
            :multiple="true"
            property="investment_types_interested"
            :v$="v$"
          />
        </div>

        <!-- Investment Amount per type -->
        <template v-if="isFundTypeSelected">
          <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Investment commitment per fund</div>
          <div class="mb-6">
            <SelectCards
              v-model="candidateForm.fund_investment_range"
              :options="investmentSizeOptions"
              :disabled="isFieldDisabled"
              property="fund_investment_range"
              :v$="v$"
              :columns="3"
            />
          </div>
        </template>

        <template v-if="isSyndicateTypeSelected">
          <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">
            Investment commitment per syndicate (SPV)
          </div>
          <div class="mb-6">
            <SelectCards
              v-model="candidateForm.spv_investment_range"
              :options="investmentSizeOptions"
              :disabled="isFieldDisabled"
              property="spv_investment_range"
              :v$="v$"
              :columns="3"
            />
          </div>
        </template>

        <template v-if="isAngelGroupTypeSelected">
          <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Investment commitment per deal</div>
          <div class="mb-6">
            <SelectCards
              v-model="candidateForm.deal_investment_range"
              :options="investmentSizeOptions"
              :disabled="isFieldDisabled"
              property="deal_investment_range"
              :v$="v$"
              :columns="3"
            />
          </div>
        </template>

        <!-- Industries of Interest -->
        <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Industries of interest</div>
        <div class="mb-6">
          <VDropdownMultiple
            aligned="left"
            class="z-auto"
            :options="industryOptions"
            property="industries_interested"
            v-model="candidateForm.industries_interested"
            :show-selected-list="true"
            :disabled="isFieldDisabled"
          >
            <VButton size="xl" class="w-full" :disabled="isFieldDisabled">
              <div class="flex items-center justify-between">
                <div class="text-gray-500">+ Add industry...</div>
                <VIcon name="chevron_selector_vertical" />
              </div>
            </VButton>
          </VDropdownMultiple>
          <p class="mt-1 text-sm text-red-500" v-if="errors.industries_interested">
            {{ errors.industries_interested }}
          </p>
        </div>

        <!-- Industries Excluded -->
        <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Industries to avoid</div>
        <div class="mb-6">
          <VDropdownMultiple
            aligned="left"
            class="z-auto"
            :options="industryOptions"
            property="industries_excluded"
            v-model="candidateForm.industries_excluded"
            :show-selected-list="true"
            :disabled="isFieldDisabled"
          >
            <VButton size="xl" class="w-full" :disabled="isFieldDisabled">
              <div class="flex items-center justify-between">
                <div class="text-gray-500">+ Add industry to exclude...</div>
                <VIcon name="chevron_selector_vertical" />
              </div>
            </VButton>
          </VDropdownMultiple>
          <p class="mt-1 text-sm text-red-500" v-if="errors.industries_excluded">
            {{ errors.industries_excluded }}
          </p>
        </div>

        <!-- University Affiliation -->
        <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">
          University affiliation (optional)
        </div>
        <p class="mb-3 text-sm text-gray-500">
          Some firms require university affiliation. If you'd like to connect with an alumni group, select your alumni
          affiliation.
        </p>
        <div>
          <VDropdownMultiple
            aligned="left"
            class="z-auto"
            :options="universityOptions"
            property="university_affiliations"
            v-model="candidateForm.university_affiliations"
            :show-selected-list="true"
            :disabled="isFieldDisabled"
          >
            <VButton size="xl" class="w-full" :disabled="isFieldDisabled">
              <div class="flex items-center justify-between">
                <div class="text-gray-500">Select university...</div>
                <VIcon name="chevron_selector_vertical" />
              </div>
            </VButton>
          </VDropdownMultiple>
        </div>
      </div>
    </div>
    <VLoadingSection
      v-else
      class="my-40"
      title="Preparing Your Information"
      description="Please wait a moment while we set things up for you. Your data is being processed to ensure everything runs
    smoothly."
    />
  </div>
  <div class="mt-24" v-if="!isFieldDisabled && !isWizard">
    <VButton variant="v-blue" size="lg" :click="submit">Confirm Changes</VButton>
  </div>
</template>
